import CloseIcon from "@mui/icons-material/Close";
import { Box, Chip, IconButton, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { type ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useWeekIndex } from "../../contexts/WeekIndexProvider";
import {
	rdoMaxAmount,
	shiftTargetAmount,
	validateRDOAmount,
	validateTotalShiftAmount,
	validateWeekendsAmount,
	weekendsTargetAmount,
} from "../../pages/NurseSchedule/utils";
import { WarningViolation } from "../../pages/NurseSchedule/WarningViolation";
import { NEUTRAL, STATUS, SURFACE } from "../../theme/tokens";
import { trackEvent } from "../../utils/analytics";
import { formatPersonFullName } from "../../utils/formatters";
import { type Day, type Nurse, type NurseInShift, type Shift, ShiftType } from "../../utils/models";
import { getNurseChanges } from "../../utils/utilFunctions";
import { checkIsDraftMode, draftWeekPeriod, getDraftModeEnd, getDraftModeStart } from "../staffing/DraftMode";

interface NurseDrawerProps {
	hideHeader?: boolean;
	onClose: () => void;
}

type tabType = "all" | "replacements" | "violations";

// Reusable chip styling for the drawer's tab chips. Selected state pulls from
// theme.palette.primary so the drawer re-tints with the user's chosen color.
const tabChipSx = (selected: boolean) => (theme: import("@mui/material/styles").Theme) => ({
	bgcolor: selected ? alpha(theme.palette.primary.main, 0.18) : "transparent",
	color: selected ? theme.palette.primary.dark : "inherit",
	"&:hover": {
		bgcolor: selected ? alpha(theme.palette.primary.main, 0.18) : NEUTRAL[100],
	},
});

const subTabSx = (selected: boolean) => (theme: import("@mui/material/styles").Theme) => ({
	py: 1,
	px: 2,
	cursor: "pointer",
	borderBottom: selected ? `2px solid ${theme.palette.primary.dark}` : "2px solid transparent",
	color: selected ? theme.palette.primary.dark : "inherit",
});

type NurseAndSummary = {
	nurse: Nurse;
	summary: {
		changes: number;
		shifts: number;
		weekendShifts: number;
		rdoCount: number;
		role: ReactNode;
		chargeShifts: number;
	};
	violations: {
		rdoViolation: boolean;
		shiftViolation: boolean;
		weekendViolation: boolean;
	};
};

export const NurseDrawer = ({ hideHeader, onClose }: NurseDrawerProps) => {
	const { t } = useTranslation();
	const { hospitalData, getNurseShifts, getNurseWeekendShifts, getNurseRDOs, getNurseRole, isHospitalDemo, chosenDepartment, getNurseFormattedName } =
		useHospitalData();
	const { weekIndex } = useWeekIndex();
	const [selectedTab, setSelectedTab] = useState<tabType>("all");
	const [selectedSubTab, setSelectedSubTab] = useState<"all" | "day" | "night">("all");
	const navigate = useNavigate();

	const draftModeEnabled = useMemo(() => checkIsDraftMode(weekIndex) && isHospitalDemo, [weekIndex, isHospitalDemo]);
	const beginningOfDraft = useMemo(() => getDraftModeStart(), []);
	const endOfDraft = useMemo(() => getDraftModeEnd(), []);

	useEffect(() => {
		if (draftModeEnabled) {
			setSelectedTab("all");
		}
	}, [draftModeEnabled]);

	const getNurseManagerShifts = useCallback(
		(nurseId: number) => {
			const nurse = hospitalData.nurses?.find((n) => n.id === nurseId);
			if (!nurse || !hospitalData.weeks) return 0;

			let chargeShiftCount = 0;
			hospitalData.weeks.slice(1).forEach((week) => {
				week.weeklyShifts.forEach((shift: Shift) => {
					shift.days.forEach((day: Day) => {
						const nurseInShift: NurseInShift | undefined = day.nurses.find(
							(n) =>
								n.nurseId === nurseId &&
								(!draftModeEnabled ||
									(n.startTime >= beginningOfDraft.startOf("day") &&
										n.startTime <= endOfDraft.endOf("day") &&
										n.endTime >= beginningOfDraft.startOf("day") &&
										n.endTime <= endOfDraft.endOf("day"))),
						);
						if (nurseInShift?.charge) {
							chargeShiftCount++;
						}
					});
				});
			});

			return chargeShiftCount;
		},
		[hospitalData.nurses, hospitalData.weeks, hospitalData, draftModeEnabled, beginningOfDraft, endOfDraft],
	);

	const handleTabChange = useCallback((tab: tabType) => {
		setSelectedTab(tab);
		trackEvent("nurse_drawer_tab_changed", {
			tab: tab,
		});
	}, []);

	const handleSubTabChange = (tab: "all" | "day" | "night") => {
		setSelectedSubTab(tab);
		trackEvent("nurse_drawer_subtab_changed", {
			tab: tab,
			filtered_nurses_count: filteredNurses.length,
		});
	};

	const handleNurseClick = (nurseId: number, nurseName: string) => {
		trackEvent("nurse_drawer_nurse_clicked", {
			nurse_id: nurseId,
			nurse_name: nurseName,
		});
		const path = `/${chosenDepartment.department_id}/employee-staffing/${nurseId}/${draftModeEnabled}`;
		navigate(path);
	};

	const handleClose = () => {
		trackEvent("nurse_drawer_closed");
		onClose();
	};

	const filteredNurses = useMemo(
		() =>
			hospitalData.nurses.filter((nurse) => {
				switch (selectedSubTab) {
					case "day":
						return nurse.shiftType === ShiftType.Day;
					case "night":
						return nurse.shiftType === ShiftType.Night;
					default:
						return true;
				}
			}),
		[selectedSubTab, hospitalData],
	);

	const getNurseChangesSummary = useCallback(
		(nurseId: number) => {
			return getNurseChanges(hospitalData, nurseId, undefined, draftModeEnabled ? beginningOfDraft : undefined, draftModeEnabled ? endOfDraft : undefined);
		},
		[hospitalData, draftModeEnabled, beginningOfDraft, endOfDraft],
	);
	const getNurseShiftsSummary = useCallback(
		(nurseId: number) => {
			return getNurseShifts(nurseId, draftModeEnabled ? beginningOfDraft : undefined, draftModeEnabled ? endOfDraft : undefined);
		},
		[draftModeEnabled, beginningOfDraft, endOfDraft, getNurseShifts],
	);
	const getNurseWeekendShiftsSummary = useCallback(
		(nurseId: number) => {
			return getNurseWeekendShifts(nurseId, draftModeEnabled ? beginningOfDraft : undefined, draftModeEnabled ? endOfDraft : undefined);
		},
		[getNurseWeekendShifts, draftModeEnabled, beginningOfDraft, endOfDraft],
	);
	const getNurseRDOsSummary = useCallback(
		(nurseId: number) => {
			return getNurseRDOs(nurseId, draftModeEnabled ? beginningOfDraft : undefined, draftModeEnabled ? endOfDraft : undefined);
		},
		[getNurseRDOs, draftModeEnabled, beginningOfDraft, endOfDraft],
	);

	const filteredNursesAndSummaries = useMemo(() => {
		const result = filteredNurses.map<NurseAndSummary>((nurse) => {
			const changesSummary = getNurseChangesSummary(nurse.id);
			const shiftsAmount = getNurseShiftsSummary(nurse.id);
			const weekendShiftsAmount = getNurseWeekendShiftsSummary(nurse.id);
			const rdoCount = getNurseRDOsSummary(nurse.id);
			return {
				nurse: nurse,
				summary: {
					changes: Math.max(changesSummary.additions.length, changesSummary.removals.length),
					shifts: shiftsAmount,
					weekendShifts: weekendShiftsAmount,
					chargeShifts: getNurseManagerShifts(nurse.id),
					rdoCount: rdoCount,
					role: getNurseRole(nurse),
				},
				violations: {
					rdoViolation: !validateRDOAmount(rdoCount) && isHospitalDemo,
					shiftViolation: !validateTotalShiftAmount(shiftsAmount) && isHospitalDemo,
					weekendViolation: !validateWeekendsAmount(weekendShiftsAmount) && isHospitalDemo,
				},
			};
		});
		return selectedTab === "violations"
			? result.filter((nurseAndSummary) => {
					return nurseAndSummary.violations.rdoViolation || nurseAndSummary.violations.shiftViolation || nurseAndSummary.violations.weekendViolation;
				})
			: result;
	}, [
		getNurseChangesSummary,
		getNurseShiftsSummary,
		getNurseWeekendShiftsSummary,
		getNurseRDOsSummary,
		filteredNurses,
		getNurseRole,
		isHospitalDemo,
		getNurseManagerShifts,
		selectedTab,
	]);

	return (
		<Box
			sx={{
				height: hideHeader ? "calc(100% - 65px)" : "100%",
				display: "flex",
				flexDirection: "column",
				position: "relative",
			}}
		>
			{/* Fixed header section */}
			<Box
				sx={{
					p: 2,
					borderBottom: `1px solid ${NEUTRAL[300]}`,
					backgroundColor: "white",
					position: "sticky",
					top: 0,
					zIndex: 1,
				}}
			>
				{!hideHeader && (
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							mb: 2,
						}}
					>
						<Typography variant="h6" sx={{ fontWeight: "medium" }}>
							Shift summary
						</Typography>
						<IconButton onClick={handleClose} size="small">
							<CloseIcon />
						</IconButton>
					</Box>
				)}

				{/* Tabs */}
				<Box sx={{ display: "flex", gap: 1 }}>
					<Chip
						label={
							draftModeEnabled ? `Show ${beginningOfDraft.toFormat("MM/DD")} - ${endOfDraft.toFormat("MM/DD")} (+${draftWeekPeriod} weeks)` : "All 6 weeks"
						}
						onClick={() => handleTabChange("all")}
						sx={tabChipSx(selectedTab === "all")}
					/>
					{draftModeEnabled ? (
						<Chip label={"Nurses with violations"} onClick={() => handleTabChange("violations")} sx={tabChipSx(selectedTab === "violations")} />
					) : (
						<Chip label="Shift replacements" onClick={() => handleTabChange("replacements")} sx={tabChipSx(selectedTab === "replacements")} />
					)}
				</Box>

				{/* Second row of tabs */}
				<Box sx={{ mt: 3 }}>
					<Box sx={{ display: "flex", gap: 2 }}>
						<Box onClick={() => handleSubTabChange("all")} sx={subTabSx(selectedSubTab === "all")}>
							<Typography sx={{ fontSize: 12 }}>All ({hospitalData.nurses.length})</Typography>
						</Box>
						<Box onClick={() => handleSubTabChange("day")} sx={subTabSx(selectedSubTab === "day")}>
							<Typography sx={{ fontSize: 12 }}>
								{t("day.nurse")} ({hospitalData.nurses.filter((nurse) => nurse.shiftType === ShiftType.Day).length})
							</Typography>
						</Box>
						<Box onClick={() => handleSubTabChange("night")} sx={subTabSx(selectedSubTab === "night")}>
							<Typography sx={{ fontSize: 12 }}>
								{t("night.nurse")} ({hospitalData.nurses.filter((nurse) => nurse.shiftType === ShiftType.Night).length})
							</Typography>
						</Box>
					</Box>
				</Box>
			</Box>
			{/* Table section */}
			<Box
				sx={{
					flex: 1,
					display: "flex",
					flexDirection: "column",
					position: "relative",
					overflowY: "auto",
					overflowX: "hidden",
					width: "inherit",
				}}
			>
				{/* Fixed table header */}
				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: "50px 65px 45px 50px 66px 45px 45px",
						gap: 0.5,
						borderBottom: `1px solid ${NEUTRAL[300]}`,
						py: 1,
						px: 2,
						bgcolor: NEUTRAL[50],
						position: "sticky",
						top: 0,
						zIndex: 1,
						width: "100%",
					}}
				>
					<Typography
						variant="body2"
						sx={{
							fontWeight: "medium",
						}}
					>
						Name
					</Typography>
					<Typography
						variant="body2"
						sx={{
							fontWeight: "medium",
						}}
					>
						Changes
					</Typography>
					<Typography
						variant="body2"
						sx={{
							fontWeight: "medium",
						}}
					>
						Shifts
					</Typography>
					<Typography
						variant="body2"
						sx={{
							fontWeight: "medium",
						}}
					>
						Charge
					</Typography>
					<Typography
						variant="body2"
						sx={{
							fontWeight: "medium",
						}}
					>
						Weekends
					</Typography>
					<Typography
						variant="body2"
						sx={{
							fontWeight: "medium",
						}}
					>
						RDOs
					</Typography>
					<Typography
						variant="body2"
						sx={{
							fontWeight: "medium",
						}}
					>
						D/N
					</Typography>
					<Typography
						variant="body2"
						sx={{
							fontWeight: "medium",
						}}
					>
						Role
					</Typography>
				</Box>

				{/* Scrollable table body */}
				<Box
					sx={{
						flex: 1,
					}}
				>
					{filteredNursesAndSummaries.map((nurseAndSummary, index) => {
						const nurse = nurseAndSummary.nurse;
						return (
							<Box
								key={nurse.id}
								sx={{
									display: "grid",
									gridTemplateColumns: "65px auto auto auto auto auto auto",
									gap: 0.5,
									borderBottom: `1px solid ${NEUTRAL[300]}`,
									py: 1,
									px: 1,
									bgcolor: index % 2 === 0 ? SURFACE : NEUTRAL[50],
									"&:hover": {
										bgcolor: NEUTRAL[100],
									},
								}}
							>
								<Typography
									variant="body2"
									noWrap
									// Analytics keeps the stable "First Last" form, whatever order the department displays.
									onClick={() => handleNurseClick(nurse.id, formatPersonFullName(nurse, false))}
									sx={{ cursor: "pointer" }}
									title={getNurseFormattedName(nurse.id)}
								>
									{getNurseFormattedName(nurse.id)}.
								</Typography>
								<Typography variant="body2" align={"center"}>
									{nurseAndSummary.summary.changes}
								</Typography>
								<WarningViolation
									renderIconWhenNoViolation={false}
									violation={`Total shifts must be at least ${shiftTargetAmount}.`}
									displayViolation={nurseAndSummary.violations.shiftViolation}
									fontSize={"12"}
									gap={"2px"}
								>
									<Typography variant="body2" align={"center"}>
										{nurseAndSummary.summary.shifts}
									</Typography>
								</WarningViolation>
								<Typography variant="body2" align={"center"}>
									{nurseAndSummary.summary.chargeShifts}
								</Typography>
								<WarningViolation
									renderIconWhenNoViolation={false}
									violation={`Weekend shifts must be at least ${weekendsTargetAmount}.`}
									displayViolation={nurseAndSummary.violations.weekendViolation}
									fontSize={"12"}
									gap={"2px"}
								>
									<Typography variant="body2" align={"center"}>
										{nurseAndSummary.summary.weekendShifts}
									</Typography>
								</WarningViolation>
								<WarningViolation
									renderIconWhenNoViolation={false}
									violation={`RDOs must not exceed ${rdoMaxAmount}.`}
									displayViolation={nurseAndSummary.violations.rdoViolation}
									fontSize={"12"}
									gap={"2px"}
								>
									<Typography variant="body2" align={"center"}>
										{nurseAndSummary.summary.rdoCount}
									</Typography>
								</WarningViolation>
								<Typography variant="body2" align={"center"} color={nurse.shiftType === ShiftType.Night ? STATUS.INFO.dark : STATUS.WARNING.dark}>
									{nurse.shiftType}
								</Typography>
								{nurseAndSummary.summary.role}
							</Box>
						);
					})}
				</Box>
			</Box>
		</Box>
	);
};
